import React from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { FormControl, OutlinedInput, FormHelperText } from '@mui/material'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import MarginWrapper from '../../features/wrappers/MarginWrapper'
import { Button } from '../../features/section-components/Button'
import { SectionHeader } from '../../features/section-components/SectionHeader'
import { TextboxMobile } from '../../features/section-components/Textbox/TextboxMobile'
import styles from './Support.module.scss'
import { LoadingIcon } from '../../features/section-components/LoadingIcon'
import JNJFooter from '../../features/jnj-footer/JNJFooter'
import { sendMail, getSupportCallStatus, resetStatus } from './supportSlice'
import Alert from '../../features/error/Alert'
import {
  setfirstName, setlastName, setMessage, setEmail, setFullsupport, setValidation
} from './supportFormSlice'

function SupportMobile({
  t
}) {
  const supportCallStatus = useSelector(getSupportCallStatus)
  const dispatch = useDispatch()
  const userFields = useSelector((state) => state.supportForm)
  const validFields = useSelector((state) => state.supportForm.validation)

  const isFormValid = Object.values(validFields).every((field) => field.isValid)
  const nonRequiredFields = ['lastName']

  const handleBlur = (field) => {
    if (!validFields[field].isValid || (!nonRequiredFields.includes(field) && userFields[field] === '')) {
      dispatch(setValidation({
        field,
        isValid: false,
        hasError: true
      }))
    }
  }

  const handleSubmit = () => {
    if (!isFormValid) return
    dispatch(sendMail({
      firstName: userFields.firstName,
      lastName: userFields.lastName,
      email: userFields.email,
      body: userFields.message
    }))
  }

  const validateRegex = (field, newValue) => {
    const regexStrings = {
      email: /^(\S+@\S+\.\S+)$/
    }

    return regexStrings[field].test(newValue)
  }

  const validateChange = (field, newValue) => {
    if (field === 'firstName') dispatch(setfirstName(newValue))
    if (field === 'lastName') dispatch(setlastName(newValue))
    if (field === 'email') dispatch(setEmail(newValue))
    if (field === 'message') dispatch(setMessage(newValue))

    if (nonRequiredFields.includes(field) && (!newValue || newValue.trim() === '')) {
      dispatch(setValidation({
        field,
        isValid: true,
        hasError: false
      }))
      return
    }
    const regexFields = ['email']
    if (regexFields.includes(field)) {
      const regexIsValid = validateRegex(field, newValue)
      dispatch(setValidation({
        field,
        isValid: regexIsValid,
        hasError: !regexIsValid
      }))
      return
    }

    if (newValue && newValue.trim() !== '') {
      dispatch(setValidation({
        field,
        isValid: true,
        hasError: false
      }))
      return
    }

    dispatch(setValidation({
      field,
      isValid: false,
      hasError: false
    }))
  }

  const handleSendAnotherMessage = () => {
    dispatch(resetStatus())
    dispatch(setFullsupport({
      firstName: '',
      lastName: '',
      email: '',
      message: ''
    }))
    dispatch(setValidation({ field: 'firstName', isValid: false, hasError: false }))
    dispatch(setValidation({ field: 'lastName', isValid: true, hasError: false }))
    dispatch(setValidation({ field: 'email', isValid: false, hasError: false }))
    dispatch(setValidation({ field: 'message', isValid: false, hasError: false }))
  }

  const theme = createTheme({
    typography: {
      fontFamily: '"JohnsonText", Arial, sans-serif',
    }
  })

  return (
    <MarginWrapper>
      <LoadingIcon isLoading={supportCallStatus === 'loading'}>
        <div className={styles['header-mobile']}>
          <SectionHeader text={t('support-tab')} size="large" />
        </div>
        <div className={styles['help-message-mobile']}>
          {t('support-help-message')}
        </div>
        <div className={styles['middle-section']}>
          <div className={styles['contact-message-mobile']}>
            <SectionHeader text={t('support-contact-message')} size="medium" />
          </div>
          {supportCallStatus === 'succeeded' && (
            <div className={styles['success-view-mobile']}>
              <Alert
                alertStyle="success-alert"
                headerText={t('support-success-header')}
                blurb={t('support-success-blurb')}
                onClose={() => {}}
                showClose
                customStyles={{ textAlign: 'left' }}
              />
              <div className={styles['success-view-mobile-button']}>
                <Button
                  text={t('support-send-another')}
                  buttonStyle="cta-red-oval-medium"
                  onClick={handleSendAnotherMessage}
                />
              </div>
            </div>
          )}
          {supportCallStatus !== 'succeeded' && (
          <>
            <div className={validFields.firstName.hasError ? styles['field-error-mobile'] : styles['field-mobile']}>
              <label htmlFor="firstName" className={styles['form-label']}>{t('support-label-first-name')}</label>
              <div className={styles['input-mobile']}>
                <TextboxMobile
                  value={userFields.firstName}
                  onChange={(e) => validateChange('firstName', e.target.value)}
                  onBlur={() => handleBlur('firstName')}
                  error={validFields.firstName.hasError}
                  supportText={t('label-mandatory-field')}
                  required
                />
              </div>
            </div>
            <div className={validFields.lastName.hasError ? styles['field-error-mobile'] : styles['field-mobile']}>
              <label htmlFor="lastName" className={styles['form-label']}>{t('support-label-last-name-optional')}</label>
              <div className={styles['input-mobile']}>
                <TextboxMobile
                  value={userFields.lastName}
                  onChange={(e) => validateChange('lastName', e.target.value)}
                  onBlur={() => handleBlur('lastName')}
                  error={validFields.lastName.hasError}
                />
              </div>
            </div>
            <div className={validFields.email.hasError ? styles['field-error-mobile'] : styles['field-mobile']}>
              <label htmlFor="email" className={styles['form-label']}>{t('support-label-email')}</label>
              <div className={styles['input-mobile']}>
                <TextboxMobile
                  value={userFields.email}
                  onChange={(e) => validateChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  error={validFields.email.hasError}
                  supportText={userFields.email === '' ? t('label-mandatory-field') : t('validation-email')}
                  required
                />
              </div>
            </div>
            <div className={validFields.message.hasError ? styles['field-error-mobile'] : styles['field-mobile']}>
              <label htmlFor="message" className={styles['form-label']}>{t('support-label-message')}</label>
              <div className={styles['message-mobile']}>
                <ThemeProvider theme={theme}>
                  <FormControl error={validFields.message.hasError} className={styles['form-control-mobile']}>
                    <OutlinedInput
                      variant="outlined"
                      multiline
                      minRows={3}
                      maxRows={3}
                      className={styles['textfield-mobile']}
                      value={userFields.message}
                      onChange={(e) => validateChange('message', e.target.value)}
                      onBlur={() => handleBlur('message')}
                      error={validFields.message.hasError}
                      required
                      sx={{
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#4545E0',
                        },
                        borderRadius: '10px'
                      }}
                    />
                    <FormHelperText>
                      {validFields.message.hasError ? t('label-mandatory-field') : ''}
                    </FormHelperText>
                  </FormControl>
                </ThemeProvider>
              </div>
            </div>
            <div className={styles['send-message-mobile']}>
              <Button
                text={(
                  <span className={styles['button-content']}>
                    {t('support-send')}
                    <ArrowForwardIcon />
                  </span>
              )}
                buttonStyle={isFormValid === true ? 'cta-red-oval' : 'cta-grey-oval'}
                onClick={handleSubmit}
                disabled={!isFormValid}
              />
            </div>
          </>
          )}
        </div>
        <div className={styles['jnj-footer']}>
          <JNJFooter text={t('FooterText')} footerSize="medium" />
        </div>
      </LoadingIcon>
    </MarginWrapper>
  )
}

SupportMobile.propTypes = {
  t: PropTypes.func.isRequired,
}

export default SupportMobile
